import { isLevelId } from '../data/ids';
import type { CoreEvent } from '../core/tick';
import type { LevelRun } from '../core/level-runner';
import { SIM_HZ } from '../data/tuning';

/**
 * Local playtesting telemetry (GDD §16.2, issue #144). Records level events (deaths per segment, active time
 * per level, abandonment, checkpoints, flaps and jump grades) so playtest reports are backed by data, and
 * #145 can tune difficulty with evidence. It is off by default, keeps everything in memory (optionally
 * sessionStorage, never the save document) and makes no network calls. It carries no personal data: every
 * event has only a type, the ms since the session started and game fields. Pure of DOM and three (it is
 * wired from `game.ts` and the test hooks).
 */

export const TELEMETRY_SCHEMA_VERSION = 1;
/** sessionStorage key: never the save key, so it is dropped with the tab. */
export const TELEMETRY_KEY = 'narcoverse:telemetry';

export interface TelemetryEvent {
  readonly type: string;
  /** Milliseconds since the session started. */
  readonly at: number;
  readonly levelId?: string;
  readonly segmentId?: string;
  readonly x?: number;
  readonly checkpointId?: string;
  readonly grade?: string;
  readonly seconds?: number;
  readonly flaps?: number;
  readonly dollars?: number;
  readonly score?: number;
}

export interface TelemetryExport {
  readonly schemaVersion: number;
  readonly sessionStart: string;
  readonly build: string;
  readonly events: readonly TelemetryEvent[];
}

export interface TelemetryDeps {
  /** Wall clock in ms; injected so tests are deterministic. */
  readonly now: () => number;
  /** Build identifier (the commit injected by Vite); a placeholder outside a real build. */
  readonly build: () => string;
  /** Optional persistence (sessionStorage in the browser, a double in tests). */
  readonly storage?: { get(key: string): string | null; set(key: string, value: string): void };
}

export interface Telemetry {
  /** Whether recording is on (off by default). */
  readonly enabled: boolean;
  enable(): void;
  disable(): void;
  /** A level run started: resets the per-level counters and records `level-start`. */
  levelStarted(run: LevelRun): void;
  /** One core event of the run in flight; the module keeps only what playtesting needs. */
  onCoreEvent(event: CoreEvent, run: LevelRun): void;
  /** PAUSA opened (the active clock stops) and RESUME (it starts again). */
  paused(): void;
  resumed(): void;
  /** The mission was abandoned from PAUSA: records `abandon` with the segment the plane was in. */
  abandoned(run: LevelRun): void;
  /** JSON snapshot for a playtest report; no personal data. */
  export(): TelemetryExport;
  /** Every recorded event, read-only, for tests. */
  events(): readonly TelemetryEvent[];
}

/** The active time in seconds of `ticks` at the fixed 60 Hz rate (GDD §15.4), excluding pauses. */
export const activeSeconds = (ticks: number): number => ticks / SIM_HZ;

/** The id of the segment that contains x, or null when the level has no segments. */
export function segmentAt(run: LevelRun, x: number): string | null {
  const segments = run.level.segmentStarts;
  let found: string | null = null;
  for (const segment of segments) if (x >= segment.start) found = segment.id;
  return found;
}

export function createTelemetry(deps: TelemetryDeps): Telemetry {
  let enabled = false;
  let sessionStart = deps.now();
  const log: TelemetryEvent[] = [];
  /** Ticks the level has been active, excluding pauses; read from the run. */
  let levelStartTick = 0;
  /** Flaps since the level started, aggregated (not one event per flap). */
  let flaps = 0;

  const record = (event: Omit<TelemetryEvent, 'at'>) => {
    if (!enabled) return;
    log.push({ ...event, at: deps.now() - sessionStart });
    if (deps.storage) deps.storage.set(TELEMETRY_KEY, JSON.stringify({ schemaVersion: TELEMETRY_SCHEMA_VERSION, events: log }));
  };

  return {
    get enabled() {
      return enabled;
    },
    enable() {
      enabled = true;
      sessionStart = deps.now();
      log.length = 0;
    },
    disable() {
      enabled = false;
    },
    levelStarted(run) {
      if (!enabled) return;
      levelStartTick = run.core.tick;
      flaps = 0;
      record({ type: 'level-start', levelId: run.level.def.id });
    },
    onCoreEvent(event, run) {
      if (!enabled) return;
      const levelId = run.level.def.id;
      switch (event.type) {
        case 'LIFE_LOST':
        case 'GAME_OVER':
          // GAME_OVER follows the last LIFE_LOST: only one death event per life.
          if (event.type === 'LIFE_LOST') {
            record({ type: 'death', levelId, segmentId: segmentAt(run, run.core.player.x) ?? undefined, x: run.core.player.x });
          }
          break;
        case 'CHECKPOINT_ACTIVATED':
          record({ type: 'checkpoint', levelId, checkpointId: String(event.checkpointId ?? ''), seconds: activeSeconds(run.core.tick - levelStartTick) });
          break;
        case 'FLAP':
          // Aggregated by level, recorded once with the completion, not one event per flap.
          flaps += 1;
          break;
        case 'LEVEL_COMPLETED':
          record({
            type: 'level-complete',
            levelId,
            seconds: activeSeconds(run.core.tick - levelStartTick),
            grade: event.jumpGrade === undefined ? undefined : String(event.jumpGrade),
            flaps,
            dollars: run.core.dollars,
            score: run.core.score,
          });
          break;
        default:
          break;
      }
    },
    paused() {
      if (!enabled) return;
      record({ type: 'pause' });
    },
    resumed() {
      if (!enabled) return;
      // A pause extends the wall clock of the level; the active time is read from ticks, so nothing else to do.
      record({ type: 'resume' });
    },
    abandoned(run) {
      if (!enabled) return;
      record({ type: 'abandon', levelId: run.level.def.id, segmentId: segmentAt(run, run.core.player.x) ?? undefined, seconds: activeSeconds(run.core.tick - levelStartTick) });
    },
    export(): TelemetryExport {
      return {
        schemaVersion: TELEMETRY_SCHEMA_VERSION,
        sessionStart: new Date(sessionStart).toISOString(),
        build: deps.build(),
        events: [...log],
      };
    },
    events() {
      return [...log];
    },
  };
}

/** The build identifier: the commit injected by Vite, or a harmless placeholder. */
export const buildId = (): string => (import.meta.env.VITE_BUILD_ID as string | undefined) ?? 'dev';

/** Whether a level id is a campaign level (the telemetry only records campaign levels). */
export const campaignLevel = (id: string): boolean => isLevelId(id);
